import type { Tier, TrackAssessmentResult } from '@backrs_ui/common-ui';

import { createApi } from '@reduxjs/toolkit/query/react';

import {
  Assignee,
  AssigneeNote,
  AssigneeNotesQueryParams,
  AssigneeSessionsQueryParams,
  BulkAssignCoachesRequest,
  BulkAssignCoachesResponse,
  InstitutionCoachesParams,
  InstitutionMemberParams,
  CoachingDashboardFiltersResponse,
  CoachingDashboardQueryParams,
  CoachSummary,
  CreateAssigneeNoteRequest,
  decodeSeatSet,
  DeleteAssigneeNoteRequest,
  ExportDownload,
  ExportReportParams,
  ExportUrlResponse,
  InstitutionMember,
  PastSession,
  SeatSet,
  TranscriptCue,
  UpdateAssigneeNoteRequest,
  UpdateMemberCoachesRequest,
} from './coachingDashboard.types';
import { baseQueryWithAuth } from '../baseQuery';
import { type MostRecentResponseSetAPIResponse } from '../userResponse/thunks/userResponse.thunk';

/** `minimumTrack` is the tier the subject's own blueprint set, and is null when they never took it. */
export type MemberTrackAssessment = Readonly<{
  result: TrackAssessmentResult;
  history: TrackAssessmentResult[];
  minimumTrack: Tier | null;
}>;

const buildListQueryString = ({ range, filter, sort }: CoachingDashboardQueryParams) => {
  const params = new URLSearchParams();

  params.append('range', JSON.stringify(range));
  if (filter) params.append('filter', JSON.stringify(filter));
  if (sort) params.append('sort', JSON.stringify([sort.field, sort.direction]));

  return params.toString();
};

const buildQFilterPath = (basePath: string, q?: string, institutionIds?: readonly number[]) => {
  const trimmed = q?.trim();
  const filter = {
    ...(trimmed ? { q: trimmed } : null),
    ...(institutionIds?.length ? { institution_ids: [...institutionIds] } : null),
  };

  if (Object.keys(filter).length === 0) return basePath;

  return `${basePath}?${new URLSearchParams({ filter: JSON.stringify(filter) }).toString()}`;
};

const parseTotalCount = (meta: unknown) =>
  parseInt((meta as { response?: Response })?.response?.headers.get('X-Total-Count') ?? '0', 10);

const isJsonContentType = (contentType: string | null | undefined) =>
  contentType?.includes('application/json') ?? false;

const isCsvContentType = (contentType: string | null | undefined) =>
  ['text/csv', 'application/octet-stream'].some((type) => contentType?.includes(type));

const isExportUrlResponse = (value: unknown): value is ExportUrlResponse => {
  const payload = value as Partial<ExportUrlResponse> | null;

  return (
    typeof payload?.download_url === 'string' &&
    payload.download_url.length > 0 &&
    typeof payload.row_count === 'number' &&
    typeof payload.filename === 'string'
  );
};

/**
 * Pull the download filename out of a Content-Disposition header, falling back
 * to a sensible default when the header is absent or unparseable.
 */
const parseFilename = (meta: unknown, fallback: string) => {
  const header =
    (meta as { response?: Response })?.response?.headers.get('content-disposition') ?? '';
  const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(header);

  return match?.[1] ? decodeURIComponent(match[1]) : fallback;
};

/**
 * Server stores note bodies as stringified Lexical editor state. Parse on read
 * so callers always work with a `NoteBody` object. If the server happens to
 * return an already-parsed object (or the string is malformed), fall back to
 * whatever we received to avoid breaking the UI.
 */
const parseNoteBody = (note: Omit<AssigneeNote, 'body'> & { body: string }): AssigneeNote => {
  if (typeof note.body !== 'string') {
    return note as unknown as AssigneeNote;
  }

  try {
    return { ...note, body: JSON.parse(note.body) };
  } catch {
    return { ...note, body: { root: { children: [], type: 'root', version: 1 } } };
  }
};

/**
 * The one place both roster reads pass through, so it is where a member that arrived
 * without seats stops being indistinguishable from one that holds none.
 */
const decodeMembers = <T extends { seats: SeatSet }>(rows: unknown): T[] =>
  (Array.isArray(rows) ? rows : []).map((row) => ({
    ...(row as T),
    seats: decodeSeatSet((row as { seats?: unknown }).seats),
  }));

export const coachingDashboardApi = createApi({
  reducerPath: 'coachingDashboardApi',
  baseQuery: baseQueryWithAuth,
  tagTypes: [
    'CoachingDashboardAssignees',
    'CoachingDashboardInstitution',
    'CoachingDashboardFilters',
    'InstitutionCoaches',
    'CoachingDashboardSessions',
    'CoachingDashboardNotes',
  ],
  endpoints: (build) => ({
    getMyAssignees: build.query<{ data: Assignee[]; total: number }, CoachingDashboardQueryParams>({
      queryFn: async (args, _queryApi, _extraOptions, baseQuery) => {
        const result = await baseQuery(
          `coach-dashboard/my-assignees?${buildListQueryString(args)}`,
        );

        if (result.error) return { error: result.error };

        return {
          data: { data: decodeMembers<Assignee>(result.data), total: parseTotalCount(result.meta) },
        };
      },
      keepUnusedDataFor: 60 * 60, // 1 hour
      providesTags: [{ type: 'CoachingDashboardAssignees' }],
    }),
    getMyInstitution: build.query<
      { data: InstitutionMember[]; total: number },
      CoachingDashboardQueryParams
    >({
      queryFn: async (args, _queryApi, _extraOptions, baseQuery) => {
        const result = await baseQuery(
          `coach-dashboard/my-institution?${buildListQueryString(args)}`,
        );

        if (result.error) return { error: result.error };

        return {
          data: {
            data: decodeMembers<InstitutionMember>(result.data),
            total: parseTotalCount(result.meta),
          },
        };
      },
      keepUnusedDataFor: 60 * 60, // 1 hour
      providesTags: [{ type: 'CoachingDashboardInstitution' }],
    }),
    getAssignee: build.query<Assignee, number>({
      query: (userId) => `coach-dashboard/my-assignees/${userId}`,
      keepUnusedDataFor: 60 * 60, // 1 hour
      providesTags: (_result, _error, userId) => [
        { type: 'CoachingDashboardAssignees', id: userId },
      ],
    }),
    getInstitutionMember: build.query<InstitutionMember, InstitutionMemberParams>({
      query: ({ userId }) => `coach-dashboard/my-institution/${userId}`,
      keepUnusedDataFor: 60 * 60, // 1 hour
      providesTags: (_result, _error, { userId }) => [
        { type: 'CoachingDashboardInstitution', id: userId },
      ],
    }),
    getCoachingDashboardFilters: build.query<CoachingDashboardFiltersResponse, undefined>({
      query: () => 'coach-dashboard/filters',
      keepUnusedDataFor: 60 * 60, // 1 hour
      providesTags: [{ type: 'CoachingDashboardFilters' }],
    }),
    getInstitutionCoaches: build.query<CoachSummary[], InstitutionCoachesParams>({
      query: (args) =>
        buildQFilterPath('coach-dashboard/institution-coaches', args?.q, args?.institutionIds),
      keepUnusedDataFor: 60 * 60, // 1 hour
      providesTags: [{ type: 'InstitutionCoaches' }],
    }),
    addMemberCoaches: build.mutation<unknown, UpdateMemberCoachesRequest>({
      query: ({ userId, coachIds }) => ({
        url: `coach-dashboard/users/${userId}/coaches`,
        method: 'POST',
        body: { coachIds },
      }),
      invalidatesTags: [
        { type: 'CoachingDashboardAssignees' },
        { type: 'CoachingDashboardInstitution' },
      ],
    }),
    bulkAssignCoaches: build.mutation<BulkAssignCoachesResponse, BulkAssignCoachesRequest>({
      query: ({ userIds, coachIds }) => ({
        url: 'coach-dashboard/my-institution/bulk-assign-coaches',
        method: 'POST',
        body: { userIds, coachIds },
      }),
      invalidatesTags: [
        { type: 'CoachingDashboardAssignees' },
        { type: 'CoachingDashboardInstitution' },
      ],
    }),
    getAssigneeSessions: build.query<
      { data: PastSession[]; total: number },
      AssigneeSessionsQueryParams
    >({
      queryFn: async ({ userId, range }, _queryApi, _extraOptions, baseQuery) => {
        const result = await baseQuery(
          `coach-dashboard/my-assignees/${userId}/sessions?range=${encodeURIComponent(JSON.stringify(range))}`,
        );

        if (result.error) return { error: result.error };

        return {
          data: { data: result.data as PastSession[], total: parseTotalCount(result.meta) },
        };
      },
      keepUnusedDataFor: 60 * 60, // 1 hour
      providesTags: (_result, _error, { userId }) => [
        { type: 'CoachingDashboardSessions', id: userId },
      ],
    }),
    getMemberBlueprint: build.query<MostRecentResponseSetAPIResponse, number>({
      query: (userId) => `coach-dashboard/my-assignees/${userId}/blueprint`,
      keepUnusedDataFor: 60 * 60, // 1 hour
    }),
    getMemberTrackAssessment: build.query<MemberTrackAssessment, number>({
      query: (userId) => `coach-dashboard/my-assignees/${userId}/track-assessment`,
      keepUnusedDataFor: 60 * 60, // 1 hour
    }),
    getTranscriptContent: build.query<TranscriptCue[], number>({
      query: (recordingId) => `coach-dashboard/recordings/${recordingId}/transcript`,
      keepUnusedDataFor: 60 * 60, // 1 hour
    }),
    removeMemberCoaches: build.mutation<unknown, UpdateMemberCoachesRequest>({
      query: ({ userId, coachIds }) => ({
        url: `coach-dashboard/users/${userId}/coaches`,
        method: 'DELETE',
        body: { coachIds },
      }),
      invalidatesTags: [
        { type: 'CoachingDashboardAssignees' },
        { type: 'CoachingDashboardInstitution' },
      ],
    }),
    getAssigneeNotes: build.query<AssigneeNote[], AssigneeNotesQueryParams>({
      query: ({ userId, q }) => buildQFilterPath(`coach-dashboard/my-assignees/${userId}/notes`, q),
      transformResponse: (response: (Omit<AssigneeNote, 'body'> & { body: string })[]) =>
        response.map(parseNoteBody),
      providesTags: (result, _error, { userId }) => [
        { type: 'CoachingDashboardNotes', id: userId },
        ...(result?.map((note) => ({ type: 'CoachingDashboardNotes' as const, id: note.id })) ??
          []),
      ],
      keepUnusedDataFor: 60 * 60,
    }),
    createAssigneeNote: build.mutation<AssigneeNote, CreateAssigneeNoteRequest>({
      query: ({ userId, body, body_plaintext, attachment_ids }) => ({
        url: `coach-dashboard/my-assignees/${userId}/notes`,
        method: 'POST',
        body: { body: JSON.stringify(body), body_plaintext, attachment_ids },
      }),
      transformResponse: (response: Omit<AssigneeNote, 'body'> & { body: string }) =>
        parseNoteBody(response),
      invalidatesTags: (_result, _error, { userId }) => [
        { type: 'CoachingDashboardNotes', id: userId },
      ],
    }),
    updateAssigneeNote: build.mutation<AssigneeNote, UpdateAssigneeNoteRequest>({
      query: ({ userId, noteId, body, body_plaintext, attachment_ids }) => ({
        url: `coach-dashboard/my-assignees/${userId}/notes/${noteId}`,
        method: 'PATCH',
        body: { body: JSON.stringify(body), body_plaintext, attachment_ids },
      }),
      transformResponse: (response: Omit<AssigneeNote, 'body'> & { body: string }) =>
        parseNoteBody(response),
      invalidatesTags: (_result, _error, { userId, noteId }) => [
        { type: 'CoachingDashboardNotes', id: userId },
        { type: 'CoachingDashboardNotes', id: noteId },
      ],
    }),
    deleteAssigneeNote: build.mutation<unknown, DeleteAssigneeNoteRequest>({
      query: ({ userId, noteId }) => ({
        url: `coach-dashboard/my-assignees/${userId}/notes/${noteId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, { userId, noteId }) => [
        { type: 'CoachingDashboardNotes', id: userId },
        { type: 'CoachingDashboardNotes', id: noteId },
      ],
    }),
    exportReport: build.mutation<ExportDownload, ExportReportParams>({
      queryFn: async ({ reportKey }, _queryApi, _extraOptions, baseQuery) => {
        const result = await baseQuery({
          url: `coach-dashboard/exports/${encodeURIComponent(reportKey)}`,
          responseHandler: async (response) =>
            isJsonContentType(response.headers.get('content-type'))
              ? response.json()
              : response.blob(),
        });

        if (result.error) return { error: result.error };

        const contentType = result.meta?.response?.headers.get('content-type');
        const unusable = {
          error: {
            status: 'CUSTOM_ERROR' as const,
            data: contentType ?? null,
            error: 'Export response is not a download payload',
          },
        };

        if (result.data instanceof Blob) {
          if (!isCsvContentType(contentType)) return unusable;

          return {
            data: {
              kind: 'blob' as const,
              blob: result.data,
              filename: parseFilename(result.meta, `${reportKey}.csv`),
            },
          };
        }

        if (!isExportUrlResponse(result.data)) return unusable;

        return {
          data: {
            kind: 'url' as const,
            url: result.data.download_url,
            filename: result.data.filename,
            rowCount: result.data.row_count,
          },
        };
      },
    }),
  }),
});

export const {
  useGetMyAssigneesQuery,
  useGetMyInstitutionQuery,
  useGetCoachingDashboardFiltersQuery,
  useGetAssigneeQuery,
  useGetAssigneeSessionsQuery,
  useGetMemberBlueprintQuery,
  useGetMemberTrackAssessmentQuery,
  useGetTranscriptContentQuery,
  useGetInstitutionMemberQuery,
  useGetInstitutionCoachesQuery,
  useAddMemberCoachesMutation,
  useBulkAssignCoachesMutation,
  useRemoveMemberCoachesMutation,
  useGetAssigneeNotesQuery,
  useCreateAssigneeNoteMutation,
  useUpdateAssigneeNoteMutation,
  useDeleteAssigneeNoteMutation,
  useExportReportMutation,
} = coachingDashboardApi;
